import { useState } from "react";
import { ClipboardList, Package, Zap, DollarSign, Leaf, RefreshCw, Download, Building2 } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";
import axios, { API } from "@/utils/api";
import { CepEnergyPanel } from "@/components/views/CepEnergyPanels";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const ConsolidatedView = ({ clientId, consolidated, summary, onRefresh }) => {
  const t = useT();
  const [downloading, setDownloading] = useState(false);
  const isAssetScoped = summary?.department_scope === "asset";

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const tokenRes = await axios.post(`${API}/clients/${clientId}/download-token`);
      const url = `${API}/clients/${clientId}/export/excel?token=${tokenRes.data.token}`;
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "Consolidated_Report.xlsx");
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
  <div>
    <div className="page-header flex-between">
      <div>
        <h2 className="page-title">{t("consolidated_title")}</h2>
        <p className="page-subtitle">{t("consolidated_subtitle")}</p>
        {/* The counters below are asset-aware. Say which set they counted, so
            a per-building denominator is not read as a per-building
            applicability judgement. */}
        <div className="scope-chip" data-testid="consolidated-scope">
          <Building2 size={13} />
          <span>{isAssetScoped ? t("scope_building") : t("scope_client")}</span>
          <span className="text-muted">
            {isAssetScoped ? t("scope_asset_note") : t("scope_client_note")}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", gap: "var(--space-sm)" }}>
        <button className="btn btn-primary" onClick={handleDownloadExcel} disabled={downloading} data-testid="download-excel-btn">
          <Download size={18} />
          {downloading ? t("downloading") || "Downloading..." : t("download_excel")}
        </button>
        <button className="btn btn-secondary" onClick={onRefresh}>
          <RefreshCw size={18} />
          {t("refresh")}
        </button>
      </div>
    </div>

    {summary && (
      <div className="stats-grid mb-lg">
        <div className="stat-card">
          <div className="stat-icon primary"><ClipboardList size={24} /></div>
          <div className="stat-value">{summary.audits_completed}/{summary.applicable_departments || summary.total_departments}</div>
          <div className="stat-label">{t("audits_completed")}</div>
          {(summary.not_applicable_departments || 0) > 0 && (
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>{summary.not_applicable_departments} N/A</div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-icon accent"><Package size={24} /></div>
          <div className="stat-value">{summary.total_equipment_items}</div>
          <div className="stat-label">{t("equipment_items")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info"><Zap size={24} /></div>
          <div className="stat-value">{summary.total_energy_consumed_kwh?.toLocaleString()}</div>
          <div className="stat-label">{t("total_energy")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning"><DollarSign size={24} /></div>
          <div className="stat-value">${summary.total_energy_cost?.toLocaleString()}</div>
          <div className="stat-label">{t("total_cost")}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success"><Leaf size={24} /></div>
          <div className="stat-value">{summary.total_co2_emissions_kg?.toLocaleString()}</div>
          <div className="stat-label">{t("co2_emissions")}</div>
        </div>
      </div>
    )}

    {/* Audit status and N/A are recorded per client even when everything
        beside them is per-building. */}
    {summary && (
      <div className="scope-caveat mb-lg" data-testid="consolidated-audit-scope-note">
        {t("audit_scope_note")}
        {isAssetScoped && (summary.not_applicable_departments || 0) > 0 && (
          <div className="mt-xs">{t("na_scope_note")}</div>
        )}
      </div>
    )}

    {/* db.ceps in the consolidated rollup. Energy only — no CO2 is derived
        from it, because the emission factor is unresolved. */}
    <CepEnergyPanel energy={summary?.energy} />

    {consolidated.length > 0 && (
      <div className="card mb-lg">
        <h4 className="card-title">{t("audit_completion_dept")}</h4>
        <div className="table-container">
          <table className="table" data-testid="consolidated-dept-table">
            <thead>
              <tr>
                <th>{t("department")}</th>
                <th>{t("status")}</th>
                <th>{t("completion")}</th>
                <th>{t("equipment")}</th>
                <th>{t("energy_kwh")}</th>
                <th>{t("co2_kg")}</th>
                <th>{t("avg_iot_power")}</th>
              </tr>
            </thead>
            <tbody>
              {consolidated.map((dept, idx) => (
                <tr key={idx} style={dept.not_applicable ? { opacity: 0.5 } : {}}>
                  <td><strong>{dept.department}</strong></td>
                  <td>
                    {dept.not_applicable ? (
                      <span className="badge badge-na">N/A</span>
                    ) : (
                      <span className={`badge badge-${dept.audit_status === "completed" ? "success" : dept.audit_status === "in_progress" ? "warning" : "neutral"}`}>
                        {dept.audit_status?.replace(/_/g, " ")}
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-xs)" }}>
                      <div className="progress-bar" style={{ width: "80px", height: "8px" }}>
                        <div className={`progress-fill ${dept.audit_completion_percentage === 100 ? "success" : ""}`} style={{ width: `${dept.audit_completion_percentage}%` }}></div>
                      </div>
                      <span style={{ fontSize: "0.8rem" }}>{dept.audit_completion_percentage}%</span>
                    </div>
                  </td>
                  <td>{dept.total_equipment_count}</td>
                  <td>{dept.total_energy_consumed_kwh?.toLocaleString()}</td>
                  <td>{dept.total_co2_emissions_kg?.toLocaleString()}</td>
                  <td>{dept.avg_iot_power_kw}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    <div className="bento-grid">
      <div className="card">
        <h4 className="card-title">{t("energy_by_dept")}</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={consolidated.map((d) => ({ name: d.department.split(" ")[0], energy: d.total_energy_consumed_kwh }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
            <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 12 }} />
            <YAxis tick={{ fill: "#526A5A", fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="energy" fill="#244C36" name="Energy (kWh)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="card">
        <h4 className="card-title">{t("co2_by_dept")}</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={consolidated.map((d) => ({ name: d.department.split(" ")[0], co2: d.total_co2_emissions_kg }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2EAE5" />
            <XAxis dataKey="name" tick={{ fill: "#526A5A", fontSize: 12 }} />
            <YAxis tick={{ fill: "#526A5A", fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="co2" fill="#C86B4F" name="CO2 (kg)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  </div>
  );
};

export default ConsolidatedView;
